import * as Sentry from "@sentry/nextjs";
import { request } from "graphql-request";
import { jsonToGraphQLQuery } from "json-to-graphql-query";
import { useInfiniteQuery, useQuery } from "react-query";
import { useSession } from "../containers/session";
import { useCurrentUser } from "../../queries/current-user/hooks";

const endpoint = process.env.NEXT_PUBLIC_API_BASE_URL_ROCKY_GRAPHQL;

// PostgreSQL 40001 = read-replica WAL conflict. Transient - succeeds on retry.
function isReplicaConflict(error) {
  const msg = error?.message || error?.response?.errors?.[0]?.message || "";
  return msg.includes("conflict with recovery");
}

export const memberRequestKeys = {
  email: true,
  createdTS: true,
  profileImgImgixURL: true,
  gender: true,
  dob: true,
  roleName: true,
  membershipStatus: true,
  lastActivityTS: true,
  trialExpirationTS: true,
  tags: true,
  note: true,
  isPushEnabled: true,
  coachRoomID: true,
  coachRoomLastMessageSentTS: true,
  coachRoomLastCoachMessageRecievedTS: true,
  coachRoomLastImpersonatedCoachMessageRecievedTS: true,
  teamRoomID: true,
  teamRoomLastMessageSentTS: true,
  activeRoomFlagTypes: true,
  avgWorkoutRating: true,
  numWorkoutsCompleted: true,
  numWorkoutsCompleted7Days: true,
  numWorkoutsCompleted14Days: true,
  numWorkoutsCompleted30Days: true,
  numWorkoutsIncomplete: true,
  lastCompletedWorkoutID: true,
  lastCompletedWorkoutWorkoutID: true,
  lastCompletedWorkoutCreatedTS: true,
  lastCompletedWorkoutCompletedTS: true,
  lastCompletedWorkoutCompletionNumber: true,
  lastCompletedWorkoutRating: true,
  teamID: true,
  numTeamJoins: true,
  utmSource: true,
  utmContent: true,
  utmCampaign: true,
  utmMedium: true,
  utmTerm: true,
  utmType: true,
  subscriptionStripeCustomerID: true,
  subscriptionStatus: true,
  subscriptionType: true,
  healthStatus: true,
  // subscriptionRenewalTS: true,
  subscriptionIsCancelingAtPeriodEnd: true,
  subscriptionCurrentPeriodEndTS: true,
  subscriptionCurrentPeriodStartTS: true,
  introTrialPeriodStartTS: true,
  introTrialPeriodEndTS: true,
  latestTrialPeriodStartTS: true,
  latestTrialPeriodEndTS: true,
  latestTrialPeriodReason: true,
  // subscriptionOfferingID: true,
  // subscriptionOfferingName: true,
  // subscriptionOfferingDurMonths: true,
  // latestPaidPeriodStartedTS: true,
  // latestPaidPeriodEndedTS: true,
  // latestPaidPeriodNumCompleted30Days: true,
  recommendedTeamIDs: true,
  allFlagsAreMuted: true,
  recentTeamHistory: {
    teamID: true,
    teamName: true,
    joinedTS: true,
  },
  flagSettings: {
    flagType: true,
    // muteStartTS: true,
    // muteEndTS: true,
  },
};

export function useDetailedUser(
  { node, filter, sort = [], active } = {},
  props = []
) {
  const { session } = useSession();
  return useQuery(
    active ? ["detailedUser", ...props] : null,
    async ({ pageParam: after = "" }) => {
      try {
        // Check if endpoint is defined
        if (!endpoint) {
          const error = new Error("GraphQL endpoint not defined");
          error.name = "EndpointMissingError";
          error.metadata = { props, filter };
          throw error;
        }

        // Check if session is available
        if (!session) {
          const error = new Error("Authentication session not available");
          error.name = "SessionMissingError";
          error.metadata = { props };
          throw error;
        }

        const query = jsonToGraphQLQuery(
          {
            query: {
              detailedUsers: {
                __args: {
                  filter,
                  sort,
                },
                debug: {
                  sqlText: true,
                },
                edges: {
                  node,
                },
              },
            },
          },
          { pretty: true }
        );

        const response = await request(
          endpoint,
          query,
          {},
          {
            Authorization: `Bearer ${session}`,
            "Content-Type": "application/json",
          }
        );

        // Validate response structure
        if (!response?.detailedUsers) {
          const error = new Error("Invalid response structure from detailedUser query");
          error.name = "InvalidResponseError";
          error.metadata = {
            responseKeys: Object.keys(response || {}),
            props,
            filter: JSON.stringify(filter).slice(0, 200)
          };
          throw error;
        }

        return response;
      } catch (error) {
        // Enhance error with additional context
        if (!error.metadata) {
          error.metadata = {};
        }

        // Add context about the query
        error.metadata.queryName = "detailedUser";
        error.metadata.filterCount = filter ? Object.keys(filter).length : 0;
        error.metadata.sortCount = sort ? sort.length : 0;
        error.metadata.requestedNodeKeys = node ? Object.keys(node).length : 0;

        // Skip Sentry capture for replica conflicts here — React Query
        // will retry up to 3x via the `retry` config below. Capturing on
        // each attempt quadruples the event count (APOLLO-2JT had 73 events
        // for ~18 real conflicts). The onError handler captures if all
        // retries exhaust.
        if (isReplicaConflict(error)) {
          throw error;
        }

        // Capture to Sentry
        Sentry.withScope((scope) => {
          scope.setTag("component", "useDetailedUser");
          scope.setTag("graphql", "query");
          scope.setTag("operation", "detailedUser");

          scope.setContext("query", {
            filterSummary: filter ? Object.keys(filter) : [],
            sortSummary: sort || [],
            nodeKeysCount: node ? Object.keys(node).length : 0,
            active
          });

          if (error.response) {
            scope.setContext("graphqlResponse", {
              status: error.response.status,
              statusText: error.response.statusText,
              headers: Object.fromEntries(
                Object.entries(error.response.headers || {}).slice(0, 5)
              ),
              hasData: !!error.response.data,
              hasErrors: !!(error.response.errors?.length)
            });
          }

          Sentry.captureException(error);
        });

        // Log error to console for local development
        console.error("[useDetailedUser] GraphQL query error:", error);

        // Rethrow the enhanced error
        throw error;
      }
    },
    {
      retry: (failureCount, error) =>
        isReplicaConflict(error) && failureCount < 3,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 5000),
      onError: (error) => {
        // Fires after the retry policy gives up. Captures replica conflicts
        // that exhausted retries — non-replica errors were already captured
        // in queryFn, so this branch only fires for the case we deliberately
        // skipped above.
        if (isReplicaConflict(error)) {
          Sentry.withScope((scope) => {
            scope.setTag("component", "useDetailedUser");
            scope.setTag("graphql", "query");
            scope.setTag("operation", "detailedUser");
            scope.setTag("error_type", "replica_conflict_after_retries");
            Sentry.captureException(error);
          });
        }
      },
    }
  );
}

export function useDetailedUsers(
  { node, filter, sort = [], active, first = 25 } = {},
  props = []
) {
  const { session, currentUser } = useCurrentUser();
  return useInfiniteQuery(
    active ? ["detailedUsers", ...props] : null,
    async ({ pageParam: after = "" }) => {
      return await request(
        currentUser?.id && endpoint,
        jsonToGraphQLQuery(
          {
            query: {
              detailedUsers: {
                __args: {
                  first,
                  after,
                  filter,
                  sort,
                },
                totalCount: true,
                pageInfo: {
                  hasPreviousPage: true,
                  hasNextPage: true,
                  startCursor: true,
                  endCursor: true,
                },
                debug: {
                  sqlText: true,
                },
                edges: {
                  cursor: true,
                  node,
                },
              },
            },
          },
          { pretty: true }
        ),
        {},
        {
          Authorization: `Bearer ${session}`,
          "Content-Type": "application/json",
        }
      );
    },
    {
      getNextPageParam: (lastPage) => {
        if (!lastPage?.detailedUsers?.edges?.length) return;
        if (!lastPage?.detailedUsers?.pageInfo?.hasNextPage) return;

        const { cursor } =
          lastPage.detailedUsers.edges[lastPage.detailedUsers.edges.length - 1];
        return cursor;
      },
    }
  );
}
